(() => {
  if (window.__hoyostarGridV6) { window.__hoyostarGridV6.scan(document); return; }
  const instances = new Map();
  class HoyostarHoverGrid {
    constructor(container) {
      this.container = container;
      this.controller = new AbortController();
      this.timers = new Set();
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.listen = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: this.controller.signal });
      this.later = (fn, ms) => {
        const timer = setTimeout(() => { this.timers.delete(timer); fn(); }, ms);
        this.timers.add(timer);
        return timer;
      };
      this.cards = this.container.querySelectorAll('.hoyostar-hover-grid__card');
      this.hoverEffectCards = this.container.querySelectorAll('.has-hover-effect');
      this.desktop = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
      this.canAnimate = this.desktop.matches && !this.reducedMotion.matches;
      this.init();
      this.listen(document, 'visibilitychange', () => { if (document.hidden) this.stop(); });
      this.listen(window, 'pagehide', () => this.stop());
      const refresh = () => { this.destroy(); mount(this.container); };
      this.listen(this.reducedMotion, 'change', refresh);
      this.listen(this.desktop, 'change', refresh);
      this.listen(this.mobile, 'change', refresh);
    }

    stop() {
      this.cards.forEach(card => { if (card.stopAnimation) card.stopAnimation(); });
      this.cursorStops?.forEach(stop => stop());
    }

    destroy() {
      this.stop();
      this.controller.abort();
      this.observer?.disconnect();
      this.timers.forEach(timer => clearTimeout(timer));
      this.timers.clear();
      this.cards.forEach(card => { delete card.startAnimation; delete card.stopAnimation; });
      this.container.classList.remove('is-enhanced');
      this.container.querySelectorAll('img[data-hyo-hydrated]').forEach(img => img.remove());
      instances.delete(this.container);
    }

    init() {
      if (this.canAnimate) this.hoverEffectCards.forEach(card => this.setupCard(card));
      this.setupEntranceAnimation();

      if (this.canAnimate) {
        this.cards.forEach(card => this.setupCursorEffect(card));
      }
    }

    setupCursorEffect(card) {
      const tracker = card.querySelector('.hoyostar-cursor-tracker');
      if (!tracker) return;

      let mouseX = 0, mouseY = 0;
      let currentX = 0, currentY = 0;
      let isHovering = false;
      let rafId = null;

      const DAMPING = 0.12;
      const ICON_SIZE = 56;

      const lerp = (start, end, factor) => start + (end - start) * factor;

      const updateLoop = () => {
        if (!isHovering || this.reducedMotion.matches) { rafId = null; return; }
        currentX = lerp(currentX, mouseX, DAMPING);
        currentY = lerp(currentY, mouseY, DAMPING);
        tracker.style.transform = `translate3d(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px, 0)`;
        if (Math.abs(currentX - mouseX) + Math.abs(currentY - mouseY) > 0.1) {
          rafId = requestAnimationFrame(updateLoop);
        } else {
          rafId = null;
        }
      };

      const onMouseEnter = (e) => {
        isHovering = true;
        const rect = card.getBoundingClientRect();
        mouseX = e.clientX - rect.left - (ICON_SIZE / 2);
        mouseY = e.clientY - rect.top - (ICON_SIZE / 2);
        currentX = mouseX;
        currentY = mouseY;
        updateLoop();
      };

      const onMouseMove = (e) => {
        const rect = card.getBoundingClientRect();
        mouseX = e.clientX - rect.left - (ICON_SIZE / 2);
        mouseY = e.clientY - rect.top - (ICON_SIZE / 2);

        const maxX = rect.width - ICON_SIZE;
        const maxY = rect.height - ICON_SIZE;
        mouseX = Math.max(0, Math.min(mouseX, maxX));
        mouseY = Math.max(0, Math.min(mouseY, maxY));
        if (isHovering && rafId === null) updateLoop();
      };

      const onMouseLeave = () => {
        isHovering = false;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
      };
      (this.cursorStops ||= []).push(onMouseLeave);

      this.listen(card, 'mouseenter', onMouseEnter);
      this.listen(card, 'mousemove', onMouseMove);
      this.listen(card, 'mouseleave', onMouseLeave);
    }

    setupEntranceAnimation() {
      if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.cards.forEach(card => card.classList.add('is-visible'));
        return;
      }
      this.container.classList.add('is-enhanced');
      const home = this.container.dataset.hyoEntrance === 'settle';
      const grid = this.container.querySelector('.hoyostar-hover-grid');
      const observer = new IntersectionObserver(entries => {
        const visible = entries.filter(entry => entry.isIntersecting);
        const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
        if (home) {
          for (let i = visible.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [visible[i], visible[j]] = [visible[j], visible[i]];
          }
        }
        visible.forEach((entry, rank) => {
          const card = entry.target;
          observer.unobserve(card);
          if (card.classList.contains('is-visible')) return;
          const index = Array.prototype.indexOf.call(this.cards, card);
          const delay = home ? Math.min(rank * (this.mobile.matches ? 70 : 90), this.mobile.matches ? 140 : 450) : (index % columns) * 70;
          this.later(() => card.classList.add('is-visible'), delay);
        });
      }, {threshold: 0.08, rootMargin: '0px 0px -20px 0px'});
      // A focused link must never stay visually hidden while waiting to enter.
      this.listen(this.container, 'focusin', event => {
        const card = event.target.closest('.hoyostar-hover-grid__card');
        if (card) { card.classList.add('is-visible'); observer.unobserve(card); }
      });

      this.observer = observer;
      this.cards.forEach((card) => observer.observe(card));
    }

    setupCard(card) {
      const cover = card.querySelector('.cover-image');
      if (!cover) return;
      const interval = Math.max(120, Number(card.dataset.frameInterval) || 200);
      const templates = [...card.querySelectorAll('template[data-hyo-frame]')];
      let frames = [], wanted = false, timer = null, index = 0;
      const show = () => frames.forEach((image, i) => image.classList.toggle('is-active', i === index));
      const start = () => {
        wanted = true;
        if (timer || !frames.length || this.controller.signal.aborted || document.hidden) return;
        index = card.dataset.randomStart === 'true' ? Math.floor(Math.random() * frames.length) : 0;
        show();
        cover.classList.add('is-hidden');
        timer = setInterval(() => { index = (index + 1) % frames.length; show(); }, interval);
      };
      const stop = () => {
        wanted = false;
        clearInterval(timer);
        timer = null;
        cover.classList.remove('is-hidden');
        frames.forEach(image => image.classList.remove('is-active'));
      };
      card.startAnimation = start;
      card.stopAnimation = stop;
      const check = event => {
        const r = card.getBoundingClientRect();
        if (Math.min(r.bottom-event.clientY,event.clientY-r.top,event.clientX-r.left,r.right-event.clientX) < 10) stop();
        else if (!wanted) start();
      };
      this.listen(card, 'mouseenter', check);
      this.listen(card, 'mousemove', check);
      this.listen(card, 'mouseleave', stop);
      this.listen(card, 'focusin', start);
      this.listen(card, 'focusout', stop);
      // Preserve desktop preloading; templates stay inert on touch/mobile devices.
      Promise.all(templates.map(template => new Promise(resolve => {
        const image = template.content.querySelector('img').cloneNode(true);
        image.dataset.hyoHydrated = '';
        image.loading = 'eager';
        let settled = false;
        const finish = async ok => {
          if (settled) return;
          settled = true;
          if (ok && image.decode) {
            try { await image.decode(); } catch { ok = false; }
          }
          resolve(ok ? image : null);
        };
        this.listen(image, 'load', () => finish(true), {once:true});
        this.listen(image, 'error', () => finish(false), {once:true});
        this.controller.signal.addEventListener('abort', () => finish(false), {once:true});
        template.after(image);
        if (image.complete) finish(image.naturalWidth > 0);
      }))).then(loaded => {
        if (this.controller.signal.aborted) return;
        frames = loaded.filter(Boolean);
        if (wanted) start();
      });
    }

  }

  function mount(container) {
    if (!instances.has(container)) instances.set(container, new HoyostarHoverGrid(container));
  }
  function scan(root) {
    if (root.matches?.('[data-hoyostar-grid]')) mount(root);
    root.querySelectorAll('[data-hoyostar-grid]').forEach(mount);
  }
  window.__hoyostarGridV6 = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), {once:true});
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [container, instance] of instances) if (event.target.contains(container)) instance.destroy();
  });
})();
